import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  fetchUserScoreHistory,
  fetchAllScoreTags,
  removeUserScore,
  addUserScore
} from '../../../actions/score';
import { useDispatch } from 'react-redux';
import { Tag, Modal, Divider, Spin, Select, Input, Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import moment from 'moment';

const Score = ({ currentUser, id, fetchUsers }) => {
  const dispatch = useDispatch();

  const [userScoreHistory, setUserScoreHistory] = useState([]);
  const [allTagsModalOpen, setAllTagsModalOpen] = useState(false);
  const [addTagModalOpen, setAddTagModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState();
  const [note, setNote] = useState('');

  const scoreTags = useSelector((state) => state.app.scoreTags);
  const userScoreTags = useMemo(() => {
    if (!userScoreHistory?.length) return [];

    const tags = [];
    userScoreHistory.forEach((item) => {
      if (item.deleted) return;

      const tag = scoreTags.find((tag) => tag.id === item.tag_id);
      if (!tag) return;

      tags.push({
        ...item,
        tag
      });
    });

    return tags;
  }, [userScoreHistory, scoreTags]);

  const tagsSelectOptions = useMemo(() => {
    return scoreTags.map((tag) => ({
      label: (
        <div style={{ display: 'flex' }}>
          <Tag color={tag.color}>{tag.score}</Tag>
          <p>{tag.id}</p>
        </div>
      ),
      value: tag.id
    }));
  }, [scoreTags]);

  useEffect(() => {
    dispatch(fetchAllScoreTags());
    fetchUserScoreHistory(id).then((history) => setUserScoreHistory(history));
  }, []);

  const openAllTagsModal = () => setAllTagsModalOpen(true);
  const closeAllTagsModal = () => setAllTagsModalOpen(false);

  const openAddTagModal = () => setAddTagModalOpen(true);
  const closeAddTagModal = () => setAddTagModalOpen(false);

  const riskScoreColor = useMemo(() => {
    if (currentUser.current_risk_score <= 19) {
      return 'blue';
    } else if (currentUser.current_risk_score <= 49) {
      return 'green';
    } else if (currentUser.current_risk_score <= 99) {
      return 'orange';
    } else if (currentUser.current_risk_score <= 199) {
      return 'red';
    } else {
      return 'volcano';
    }
  }, [currentUser.current_risk_score]);
  const riskScoreLabel = useMemo(() => {
    if (currentUser.current_risk_score <= 19) {
      return 'Low risk';
    } else if (currentUser.current_risk_score <= 49) {
      return 'Medium-low risk';
    } else if (currentUser.current_risk_score <= 99) {
      return 'Medium-high risk';
    } else if (currentUser.current_risk_score <= 199) {
      return 'High risk';
    } else {
      return 'Unacceptable risk';
    }
  }, [currentUser.current_risk_score]);

  const onTagClick = () => {
    openAllTagsModal();
  };

  const removeScore = async (score) => {
    setLoading(true);
    removeUserScore(score)
      .then(async () => {
        await fetchUserScoreHistory(id).then((history) => setUserScoreHistory(history));
        await fetchUsers();
        message.success('Score removed');
      })
      .finally(() => setLoading(false));
  };

  const onAddClick = async () => {
    openAddTagModal();
  };

  const onSelectChange = (value) => {
    setSelectedTag(value);
  };

  const addScore = async () => {
    setLoading(true);
    await dispatch(
      addUserScore({
        user_id: id,
        tag_id: selectedTag,
        note
      })
    )
      .then(async () => {
        await fetchUserScoreHistory(id).then((history) => setUserScoreHistory(history));
        await fetchUsers();
        setNote('');
        setSelectedTag(undefined);
        message.success('Score added');
      })
      .finally(() => setLoading(false));
    closeAddTagModal();
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <Tag
        bordered={false}
        color={riskScoreColor}
        onClick={onTagClick}
        style={{ cursor: 'pointer' }}>
        {riskScoreLabel}
      </Tag>
      {userScoreTags.map((item) => (
        <Tag
          bordered={false}
          color={item?.tag?.color}
          onClick={onTagClick}
          style={{ cursor: 'pointer' }}>
          {item.tag.id}
        </Tag>
      ))}
      <Tag style={{ cursor: 'pointer' }} onClick={onAddClick}>
        +
      </Tag>

      <Modal title='Risk Score' open={allTagsModalOpen} onCancel={closeAllTagsModal} footer={null}>
        <Spin spinning={loading} tip='Loading...'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p style={{ fontSize: 16 }}>Onboarding Risk Score</p>
              <div style={{ display: 'flex', gap: 60 }}>
                <p style={{ fontSize: 16 }}>
                  {currentUser.sumsub_score !== 0 && (currentUser.sumsub_score > 0 ? '+' : '-')}
                  {Math.abs(currentUser.sumsub_score)}
                </p>
                <div></div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginTop: 20,
                maxHeight: 400,
                overflowY: 'scroll'
              }}>
              <Divider style={{ margin: 0, borderBlockStart: '1px solid rgba(0, 0, 0, 0.2)' }} />
              {userScoreTags.map((item) => (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', paddingRight: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <p style={{ fontSize: 16 }}>{item.tag.id}</p>
                      <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                        <p style={{ fontSize: 16 }}>
                          {item.tag.score > 0 ? '+' : '-'}
                          {Math.abs(item.tag.score)}
                        </p>
                        <CloseOutlined
                          style={{ cursor: 'pointer', fontSize: 16 }}
                          onClick={() => removeScore(item)}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, opacity: 0.5, fontSize: 14 }}>
                      <p>Date:</p>
                      <p>{moment(item.createdAt).format('DD.MM.YYYY')}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 6, opacity: 0.5, fontSize: 14 }}>
                      <p>Note:</p>
                      <p>{item.note}</p>
                    </div>
                  </div>
                  <Divider
                    style={{ margin: 0, borderBlockStart: '1px solid rgba(0, 0, 0, 0.2)' }}
                  />
                </>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <p style={{ fontSize: 16, fontWeight: 'bold' }}>TOTAL</p>
            <div style={{ display: 'flex', gap: 60 }}>
              <p style={{ fontSize: 16, fontWeight: 'bold' }}>
                {currentUser.sumsub_score !== 0 && (currentUser.current_risk_score > 0 ? '+' : '-')}
                {Math.abs(currentUser.current_risk_score)}
              </p>
              <div></div>
            </div>
          </div>
        </Spin>
      </Modal>

      <Modal title='Add tag' open={addTagModalOpen} onCancel={closeAddTagModal} footer={null}>
        <Select
          placeholder='Select tag'
          style={{ width: '100%', marginTop: 20 }}
          onChange={onSelectChange}
          value={selectedTag}
          options={tagsSelectOptions}
          allowClear
        />
        <Input
          style={{ marginTop: 20 }}
          placeholder='Note'
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button style={{ marginTop: 40, width: '100%' }} type='primary' onClick={addScore}>
          Add
        </Button>
      </Modal>
    </div>
  );
};

export default Score;
